import { useMutation } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useWorkspace } from '../app/WorkspaceProvider';
import { displayNameOf, useAuth } from '../auth/AuthProvider';
import { AppLink } from '../components/Nav';
import { PlusIcon, UsersIcon } from '../components/icons';
import {
  Badge,
  Banner,
  Button,
  EmptyState,
  ErrorState,
  ErrorText,
  Loading,
  PageHeader,
} from '../components/ui';
import { errorMessage } from '../lib/api';
import { countLabel, shortDate } from '../lib/format';
import { roleLabel } from '../queries/orgs';

/**
 * Settings — deliberately short. Your account comes from the identity provider
 * and your teams come from the API; there is nothing else to configure yet, and
 * inventing switches that do nothing would be worse than an honest gap.
 */

function providerLabel(providerId: string | undefined): string {
  switch (providerId) {
    case 'google.com':
      return 'Google';
    case 'password':
      return 'Email and password';
    case undefined:
      return 'Not recorded';
    default:
      return providerId;
  }
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-hairline py-3 last:border-b-0 sm:flex-row sm:items-baseline sm:gap-4">
      <dt className="w-44 shrink-0 text-caption text-chalk-faint">{label}</dt>
      <dd className="min-w-0 break-words text-body text-chalk">{children}</dd>
    </div>
  );
}

export function SettingsPage() {
  const { user, signOut } = useAuth();
  const { orgs, loading, error, refetch } = useWorkspace();

  const leave = useMutation({ mutationFn: () => signOut() });

  if (!user) {
    return (
      <>
        <PageHeader title="Settings" />
        <Banner tone="info" title="You are signed out">
          Sign in again to see your account details.
        </Banner>
      </>
    );
  }

  const created = user.metadata.creationTime;

  return (
    <>
      <PageHeader title="Settings" subtitle="Your account, and the teams you belong to." />

      <div className="space-y-6">
        <section className="card p-4 sm:p-5">
          <h2 className="text-h4 text-chalk">Account</h2>
          <p className="mt-1 text-caption text-chalk-dim">
            These come from however you signed in. To change your name or password, use that
            provider.
          </p>
          <dl className="mt-3">
            <Row label="Name">{displayNameOf(user)}</Row>
            <Row label="Email">{user.email ?? 'No email on this account'}</Row>
            <Row label="Signs in with">
              {providerLabel(user.providerData[0]?.providerId)}
            </Row>
            <Row label="Account created">
              {created ? <span className="tnum">{shortDate(created)}</span> : 'Not recorded'}
            </Row>
          </dl>
        </section>

        <section className="card p-4 sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-h4 text-chalk">Teams</h2>
            <AppLink to="/org/new">
              <Button size="sm">
                <PlusIcon size={13} />
                New team
              </Button>
            </AppLink>
          </div>

          {loading ? <Loading label="Loading your teams" /> : null}

          {error ? (
            <div className="mt-3">
              <ErrorState
                title="Could not load your teams"
                message={errorMessage(error)}
                onRetry={refetch}
              />
            </div>
          ) : null}

          {!loading && !error && orgs.length === 0 ? (
            <div className="mt-3">
              <EmptyState
                title="You are not on a team yet"
                icon={<UsersIcon size={24} />}
                action={
                  <AppLink to="/org/new">
                    <Button variant="primary">Create a team</Button>
                  </AppLink>
                }
              >
                <p>
                  Your personal library works on its own and always will. A team is for recordings
                  a group should be able to watch without you forwarding a link each time.
                </p>
              </EmptyState>
            </div>
          ) : null}

          {!loading && !error && orgs.length > 0 ? (
            <ul className="mt-2">
              {orgs.map((org) => (
                <li
                  key={org.id}
                  className="flex flex-wrap items-center justify-between gap-2 border-b border-hairline py-3 last:border-b-0"
                >
                  <div className="min-w-0">
                    <AppLink
                      to="/org/$slug"
                      params={{ slug: org.slug }}
                      className="block truncate text-body text-chalk hover:underline"
                    >
                      {org.name}
                    </AppLink>
                    <p className="text-caption text-chalk-faint tnum">
                      {countLabel(org.seatCount, 'seat')}
                    </p>
                  </div>
                  <Badge tone={org.role === 'user' ? 'neutral' : 'info'}>
                    {roleLabel(org.role)}
                  </Badge>
                </li>
              ))}
            </ul>
          ) : null}
        </section>

        <section className="card p-4 sm:p-5">
          <h2 className="text-h4 text-chalk">Not here yet</h2>
          <p className="mt-1 text-body text-chalk-dim">
            Notification preferences, a profile photo, and deleting your account are not built yet —
            the API has no home for them, so there is nothing on this page pretending otherwise. If
            you need an account removed, ask us and we will do it by hand.
          </p>
        </section>

        <section className="card p-4 sm:p-5">
          <h2 className="text-h4 text-chalk">Sign out</h2>
          <p className="mt-1 text-body text-chalk-dim">
            Ends the session in this browser. Your recordings and share links are unaffected.
          </p>
          <Button
            variant="danger"
            className="mt-4"
            busy={leave.isPending}
            onClick={() => leave.mutate()}
          >
            Sign out
          </Button>
          {leave.isError ? (
            <div className="mt-2">
              <ErrorText>{errorMessage(leave.error)}</ErrorText>
            </div>
          ) : null}
        </section>
      </div>
    </>
  );
}
